import { Directive, ElementRef, HostListener, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

const OPEN_DELAY_MS = 100;
const CLOSE_DELAY_MS = 250;

// Desktop mouse with the navbar expanded (Bootstrap xl). Touch and the mobile navbar keep click-to-open.
const canHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1200px)');

/**
 * Opens a mat-menu on hover: <button [matMenuTriggerFor]="menu" appHoverMenuTrigger>.
 * Clicking still works; leaving the trigger and the panel closes the menu.
 */
@Directive({
  selector: '[matMenuTriggerFor][appHoverMenuTrigger]',
})
export class HoverMenuTriggerDirective implements OnInit, OnDestroy {
  // Only one hover menu is open at a time, so moving along the navbar swaps menus.
  private static current: HoverMenuTriggerDirective | null = null;

  private openTimer: any;
  private closeTimer: any;
  private hoverOpen = false;
  private previousFocus: Element | null = null;
  private listeners: (() => void)[] = [];
  private closedSub: Subscription;

  constructor(
    private trigger: MatMenuTrigger,
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    // Runs before MatMenuTrigger's click handler, which would otherwise toggle the hover-opened menu shut.
    this.el.nativeElement.addEventListener('click', this.keepOpenOnClick, true);
    this.closedSub = this.trigger.menuClosed.subscribe(() => this.onClosed());
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('click', this.keepOpenOnClick, true);
    this.closedSub.unsubscribe();
    this.trigger.closeMenu();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!canHover.matches) {
      return;
    }
    clearTimeout(this.closeTimer);
    if (!this.trigger.menuOpen) {
      this.openTimer = setTimeout(() => this.open(), OPEN_DELAY_MS);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    clearTimeout(this.openTimer);
    this.scheduleClose();
  }

  private keepOpenOnClick = (event: MouseEvent): void => {
    clearTimeout(this.openTimer);
    if (this.hoverOpen) {
      event.stopImmediatePropagation();
    }
  };

  private open(): void {
    if (this.trigger.menuOpen) {
      return;
    }
    // When swapping menus, keep the focus from before the first one opened.
    const previous = HoverMenuTriggerDirective.current;
    this.previousFocus = previous ? previous.previousFocus : document.activeElement;
    previous?.trigger.closeMenu();
    HoverMenuTriggerDirective.current = this;
    this.hoverOpen = true;

    // The backdrop would cover the trigger and fire mouseleave as soon as the menu opens.
    this.trigger.menu.hasBackdrop = false;
    // Material focuses the first item on open; a 'mouse' origin keeps it from looking selected.
    (this.trigger as any)._openedBy = 'mouse';
    this.trigger.restoreFocus = false;
    this.trigger.openMenu();

    // The panel is rendered on the next change detection pass.
    this.zone.onStable.pipe(take(1)).subscribe(() => {
      const panel = document.getElementById(this.trigger.menu.panelId);
      if (!panel) {
        return;
      }
      this.zone.run(() => this.listeners.push(
        this.renderer.listen(panel, 'mouseenter', () => clearTimeout(this.closeTimer)),
        this.renderer.listen(panel, 'mouseleave', () => this.scheduleClose()),
        // No backdrop, so close on clicks elsewhere on the page ourselves.
        this.renderer.listen('document', 'pointerdown', (event: PointerEvent) => {
          const target = event.target as Node;
          if (!panel.contains(target) && !this.el.nativeElement.contains(target)) {
            this.trigger.closeMenu();
          }
        }),
      ));
    });
  }

  private scheduleClose(): void {
    if (!this.hoverOpen) {
      return;
    }
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.trigger.closeMenu();
      // The mouse just wandered off, so give focus back to whatever had it (e.g. a field being typed in).
      (this.previousFocus as HTMLElement)?.focus?.({ preventScroll: true });
    }, CLOSE_DELAY_MS);
  }

  private onClosed(): void {
    clearTimeout(this.closeTimer);
    this.listeners.forEach(unlisten => unlisten());
    this.listeners = [];
    if (this.hoverOpen) {
      // Material's default for a top-level menu (the setter turns undefined into false).
      this.trigger.menu.hasBackdrop = true;
      this.trigger.restoreFocus = true;
      this.hoverOpen = false;
    }
    if (HoverMenuTriggerDirective.current === this) {
      HoverMenuTriggerDirective.current = null;
    }
  }
}
